import { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/lib/store';
import { api } from '@/lib/api';
import LoadingSpinner from '@/components/LoadingSpinner';
import type { TelegramAuthData } from '@/types';

interface LocationState {
  registrationToken: string;
  walletAddress?: string;
  telegramData?: TelegramAuthData;
  method: 'wallet' | 'telegram';
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuth, isAuthenticated } = useAuthStore();

  const state = location.state as LocationState | null;

  const [manualWallet, setManualWallet] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If already authenticated, redirect to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // If no registration state, redirect to login
  if (!state?.registrationToken) {
    return <Navigate to="/login" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // For telegram registration, we can optionally provide a wallet address
      const walletAddress =
        state.method === 'wallet'
          ? state.walletAddress
          : manualWallet.trim() || undefined;

      // Validate wallet address format if provided
      if (walletAddress && !/^0x[a-fA-F0-9]{40}$/.test(walletAddress)) {
        throw new Error('Invalid wallet address format');
      }

      const result = await api.completeRegistration(
        state.registrationToken,
        walletAddress
      );

      if (result.success) {
        setAuth(result.user, result.sessionToken);
        navigate('/dashboard');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-2xl mb-4">
            <span className="text-2xl font-bold text-white">LT</span>
          </div>
          <h1 className="text-3xl font-bold text-white">Welcome to LazaiTrader</h1>
          <p className="text-dark-400 mt-2">Complete your registration</p>
        </div>

        {/* Registration Card */}
        <div className="card">
          <h2 className="text-xl font-semibold text-white mb-6">
            {state.method === 'wallet' ? 'Wallet Connected' : 'Telegram Connected'}
          </h2>

          {/* Connected Info */}
          <div className="bg-dark-900 rounded-lg p-4 mb-6">
            {state.method === 'wallet' && state.walletAddress && (
              <div>
                <p className="text-sm text-dark-400 mb-1">Connected Wallet</p>
                <p className="font-mono text-white">
                  {state.walletAddress.slice(0, 10)}...{state.walletAddress.slice(-8)}
                </p>
              </div>
            )}
            {state.method === 'telegram' && state.telegramData && (
              <div>
                <p className="text-sm text-dark-400 mb-1">Telegram Account</p>
                <p className="text-white">
                  {state.telegramData.first_name}{' '}
                  {state.telegramData.last_name || ''}
                  {state.telegramData.username && (
                    <span className="text-dark-400 ml-2">
                      @{state.telegramData.username}
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 mb-6 text-red-200 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Optional wallet input for Telegram users */}
            {state.method === 'telegram' && (
              <div className="mb-6">
                <label className="label">
                  Wallet Address (Optional)
                </label>
                <input
                  type="text"
                  value={manualWallet}
                  onChange={(e) => setManualWallet(e.target.value)}
                  placeholder="0x..."
                  className="input font-mono"
                />
                <p className="text-xs text-dark-400 mt-2">
                  Optionally link a wallet address to your account. You can also do this later.
                </p>
              </div>
            )}

            {/* What happens next */}
            <div className="bg-dark-700/50 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-medium text-white mb-2">
                What happens next?
              </h3>
              <ul className="text-sm text-dark-300 space-y-2">
                <li className="flex items-start space-x-2">
                  <span className="text-primary-500">•</span>
                  <span>A Smart Contract Wallet (SCW) will be created for you</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-primary-500">•</span>
                  <span>You can deposit funds and configure trading strategies</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-primary-500">•</span>
                  <span>The bot will automatically execute trades based on your settings</span>
                </li>
              </ul>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn btn-primary py-3 text-lg font-medium flex items-center justify-center"
            >
              {isSubmitting ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          {/* Back link */}
          <button
            onClick={() => navigate('/login')}
            className="w-full mt-4 text-sm text-dark-400 hover:text-white transition-colors"
          >
            ← Back to login
          </button>
        </div>
      </div>
    </div>
  );
}
